import type { ComponentProps } from 'react';

import { Link, useParams } from 'react-router';

import { appConfig, supportedLocales, type AppLocale } from '@/shared/configs/app.config';
import { getLocaleUrl } from '@/shared/utils/i18n.utils';

function getLocalizedTo(to: string | { pathname?: string }, currentLocale: AppLocale) {
  if (typeof to === 'string') {
    return getLocaleUrl(currentLocale, to);
  }
  if (to.pathname) {
    return { ...to, pathname: getLocaleUrl(currentLocale, to.pathname) };
  }
  return to;
}

type LocaleLinkProps = ComponentProps<typeof Link>;

/**
 * A locale-aware Link component that automatically prefixes
 * the current locale to the href.
 *
 * @example
 * // If current URL is /sl/configure/123
 * <LocaleLink to="/success">Done</LocaleLink>
 * // Renders link to /sl/success
 */
export const LocaleLink = ({ to, ...props }: LocaleLinkProps) => {
  const params = useParams<{ locale?: string }>();

  // Get current locale from URL params or extract from pathname
  const currentLocale: AppLocale =
    params.locale && supportedLocales.includes(params.locale as AppLocale)
      ? (params.locale as AppLocale)
      : appConfig.fallbackLanguage;

  // Build the localized URL
  const localizedTo = getLocalizedTo(to, currentLocale);

  return <Link to={localizedTo} {...props} />;
};
